import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Layers, UserPlus, Mail, Phone, MapPin, Clock,
  CheckCircle2, XCircle, RefreshCw, Filter, Send, Inbox, Hourglass, ThumbsUp, ThumbsDown, Plus, Trash2, Archive, ArchiveRestore, Search, Upload,
} from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmDialog';
import { MatchInstallersModal } from '@/components/leads/MatchInstallersModal';
import { LEAD_SOURCES } from '@/config/leadSources';
import { formatApiError } from '@/utils/apiError';
import LeadCsvImportModal from '@/components/LeadCsvImportModal';
import { useLeadDraft, useAutosaveDraft } from '@/hooks/useLeadDraft';
import { LeadDraftBanner } from '@/components/leads/LeadDraftBanner';

const DRAFT_FIELDS = ['customer_name', 'customer_phone', 'customer_email', 'customer_address', 'project_description'];

const SECTION_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor', general: 'General / Other' };
const SECTION_COLOR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#D4A24B', general: '#6B7280' };

// Tile metadata for the four lead statuses — mirrors the StaffOrdersQueue pattern.
const TILES = [
  { key: 'unassigned', label: 'New / Unassigned',  sub: 'Ready to triage',         icon: Inbox,      color: '#1d4ed8' },
  { key: 'pending',    label: 'Assigned / Pending', sub: 'Awaiting installer reply', icon: Hourglass, color: '#b45309' },
  { key: 'accepted',   label: 'Accepted',          sub: 'With installer',          icon: ThumbsUp,   color: '#047857' },
  { key: 'rejected',   label: 'Rejected',          sub: 'Needs re-assignment',     icon: ThumbsDown, color: '#b91c1c' },
  { key: 'archived',   label: 'Archived',          sub: 'Older than 30 days',      icon: Archive,    color: '#475569' },
];

export default function LeadManagement() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const confirm = useConfirm();
  const [sp, setSp] = useSearchParams();
  const activeKey = sp.get('status') || 'unassigned';
  const activeTile = TILES.find(t => t.key === activeKey) || TILES[0];

  const [board, setBoard] = useState({ columns: { unassigned: [], pending: [], accepted: [], rejected: [], archived: [] }, totals: {} });
  const [loading, setLoading] = useState(true);
  const [sectionFilter, setSectionFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [assignLead, setAssignLead] = useState(null);
  const [matchLead, setMatchLead] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [submittingAssign, setSubmittingAssign] = useState(false);

  // Add-lead modal
  const EMPTY_LEAD = { customer_name: '', customer_phone: '', customer_email: '', customer_address: '', service_type: 'probound', project_description: '', source: '' };
  const [showAdd, setShowAdd] = useState(false);
  const [newLead, setNewLead] = useState(EMPTY_LEAD);
  const [creating, setCreating] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const { draft, save: saveDraft, clear: clearDraft } = useLeadDraft('lm_lead_draft', DRAFT_FIELDS);
  const openAdd = () => {
    if (draft) { setNewLead({ ...EMPTY_LEAD, ...draft.data }); toast.info('Restored your unfinished lead'); } else { setNewLead(EMPTY_LEAD); }
    setShowAdd(true);
  };
  const addDirty = DRAFT_FIELDS.some((k) => (newLead[k] || '').trim() !== '');
  useAutosaveDraft(showAdd && addDirty, newLead, saveDraft);
  const closeAdd = async () => {
    if (addDirty) {
      const ok = await confirm({ title: 'Discard this lead?', body: "You've started filling in the form — closing it now will lose what you've typed.\n\nTip: closing the tab or reloading instead keeps it saved as a draft.", confirmLabel: 'Discard', cancelLabel: 'Keep editing', tone: 'danger' });
      if (!ok) return;
      clearDraft();
    }
    setShowAdd(false);
  };

  const submitAdd = async () => {
    if (!newLead.customer_name.trim()) { toast.error('Customer name is required'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLead),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to add lead');
      toast.success(`Lead added — ${data.reference || data.customer_name}`);
      clearDraft();
      setShowAdd(false);
      // New leads land unassigned — jump there so the user sees their entry.
      setSp({ status: 'unassigned' });
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteLead = async (lead) => {
    const ok = await confirm({
      title: `Delete lead "${lead.customer_name}"?`,
      body: `${lead.reference ? `Reference ${lead.reference}` : 'No reference yet'} · this cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/leads/${lead.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to delete lead');
      toast.success(`Lead deleted`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const archiveLead = async (lead) => {
    const ok = await confirm({
      title: `Archive "${lead.customer_name}"?`,
      body: `It'll move to the Archived tab. You can restore it any time.`,
      confirmLabel: 'Archive',
      tone: 'default',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/leads/${lead.id}/archive`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to archive');
      toast.success(`Lead archived`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const restoreLead = async (lead) => {
    try {
      const res = await fetch(`${API_URL}/api/leads/${lead.id}/restore`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to restore');
      toast.success(`Lead restored`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/leads-board${sectionFilter ? `?section=${sectionFilter}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load board');
      const data = await res.json();
      setBoard(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [sectionFilter]);

  useEffect(() => {
    if (!isStaff) {
      navigate('/');
      return;
    }
    load();
  }, [isStaff, navigate, load]);

  const visibleLeads = useMemo(() => {
    const all = board.columns?.[activeKey] || [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return all;
    return all.filter(l => {
      const blob = [l.customer_name, l.customer_phone, l.customer_email, l.customer_address, l.reference, l.project_description]
        .filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [board, activeKey, searchQuery]);

  const counts = useMemo(() => ({
    unassigned: (board.columns?.unassigned || []).length,
    pending:    (board.columns?.pending    || []).length,
    accepted:   (board.columns?.accepted   || []).length,
    rejected:   (board.columns?.rejected   || []).length,
    archived:   (board.columns?.archived   || []).length,
  }), [board]);

  const openAssign = (lead) => {
    setAssignLead(lead);
    setCodeInput('');
  };

  const submitAssign = async () => {
    if (!assignLead) return;
    const codes = codeInput
      .split(/[\s,;]+/)
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    if (codes.length === 0) {
      toast.error('Enter at least one 6-character installer account code');
      return;
    }
    setSubmittingAssign(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/${assignLead.id}/assign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_codes: codes, section: assignLead.service_type }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to assign');
      const okCount = (data.created || []).length;
      const overrideCount = (data.created || []).filter((o) => o.unapproved_override).length;
      const skipped = data.skipped || [];
      if (okCount > 0) {
        const overrideNote = overrideCount > 0
          ? ` (${overrideCount} approved in principle — tagged for audit)`
          : '';
        toast.success(`Offered to ${okCount} installer${okCount === 1 ? '' : 's'}${overrideNote}`);
      }
      if (skipped.length > 0) {
        toast.warning(skipped.map((s) => `${s.code}: ${s.reason}`).join(' · '));
      }
      setAssignLead(null);
      setCodeInput('');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingAssign(false);
    }
  };

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #7A0B7A 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-3">
          <button onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg shrink-0" data-testid="lm-back" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <Layers className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} />
              Lead Management
            </h1>
            <p className="hidden sm:block text-white/70 text-xs sm:text-sm mt-0.5">
              Triage incoming enquiries · assign to Approved Installers by account code · first-to-accept wins
            </p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" data-testid="lm-home" aria-label="Home">
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      {/* Section filter */}
      {draft && !showAdd && (
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 pt-3">
          <LeadDraftBanner draft={draft} onResume={openAdd} onDiscard={clearDraft} />
        </div>
      )}
      <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mr-1">
          <Filter className="w-3.5 h-3.5" /> Filter:
        </div>
        {[['', 'All'], ['probound', 'ProBound'], ['proepoxy', 'ProEpoxy'], ['prodecor', 'ProDecor']].map(([val, label]) => (
          <button
            key={val || 'all'}
            data-testid={`lm-filter-${val || 'all'}`}
            onClick={() => setSectionFilter(val)}
            className={`text-xs px-3 py-1.5 rounded-full border-2 transition-colors ${
              sectionFilter === val
                ? 'border-[#7A0B7A] bg-[#7A0B7A] text-white font-semibold'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
        <Button onClick={load} size="sm" variant="outline" className="ml-auto" data-testid="lm-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
        <Button onClick={() => setShowImport(true)} size="sm" variant="outline" className="border-2" data-testid="lm-import-csv">
          <Upload className="w-4 h-4 mr-1.5" /> Import CSV
        </Button>
        <Button onClick={openAdd} size="sm" className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white" data-testid="lm-add-lead">
          <Plus className="w-4 h-4 mr-1.5" /> Add lead
        </Button>
      </div>

      <div className="max-w-[1500px] mx-auto px-3 sm:px-4 pb-8">
        {/* Status tiles — clicking narrows the list below, same UX as the Invoice Queue */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
          {TILES.map(t => {
            const isActive = activeKey === t.key;
            return (
              <button
                key={t.key}
                data-testid={`lm-tile-${t.key}`}
                onClick={() => setSp({ status: t.key })}
                className={`rounded-xl p-4 text-left border-2 transition-all bg-white ${isActive ? 'border-gray-900 shadow' : 'border-transparent hover:border-gray-200'}`}
              >
                <div className="text-xs uppercase tracking-wider font-bold" style={{ color: t.color }}>{t.label}</div>
                <div className="text-3xl font-bold mt-1">{counts[t.key]}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{t.sub}</div>
              </button>
            );
          })}
        </div>

        {/* Single-status list */}
        <Card>
          <div className="p-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <activeTile.icon className="w-5 h-5 text-gray-700" />
            <h2 className="font-bold text-gray-900">{activeTile.label}</h2>
            <span className="text-sm text-gray-500">— {visibleLeads.length} lead{visibleLeads.length === 1 ? '' : 's'}{searchQuery ? ` matching “${searchQuery}”` : ''}</span>
            <div className="ml-auto relative w-full sm:w-64">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, phone, ref, address…"
                className="pl-9 pr-8 h-9 text-sm"
                data-testid="lm-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  aria-label="Clear search"
                  data-testid="lm-search-clear"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-gray-500">Loading…</div>
          ) : visibleLeads.length === 0 ? (
            <div className="p-10 text-center text-gray-500 italic">
              {searchQuery ? `— nothing matches “${searchQuery}” in this queue —` : '— no leads in this queue —'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
              {visibleLeads.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  column={activeKey}
                  onAssign={() => openAssign(lead)}
                  onMatch={() => setMatchLead(lead)}
                  onDelete={() => deleteLead(lead)}
                  onArchive={() => archiveLead(lead)}
                  onRestore={() => restoreLead(lead)}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Assign modal */}
      {matchLead && <MatchInstallersModal lead={matchLead} onClose={() => setMatchLead(null)} onChanged={load} />}
      {assignLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAssignLead(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-[#7A0B7A]" /> Assign Lead to Installer(s)
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Ref <code className="font-bold">{assignLead.reference || '—'}</code> · {assignLead.customer_name} · {SECTION_LABEL[assignLead.service_type] || assignLead.service_type}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label htmlFor="lm-codes" className="text-sm font-semibold text-gray-700">
                  Installer account codes (6-character)
                </Label>
                <Textarea
                  id="lm-codes"
                  data-testid="lm-assign-codes"
                  rows={3}
                  placeholder={'ABC234 \nXY78H9\n(one per line, or comma/space separated)'}
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  className="mt-1 font-mono uppercase tracking-wider text-sm"
                />
                <p className="text-[11px] text-gray-500 mt-1.5">
                  Add as many codes as you like. Each installer receives an email + red badge.
                  <strong> First to accept wins</strong> — the others are automatically cancelled.
                </p>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-400 rounded-md px-3 py-2 text-[11px] text-blue-900">
                <strong>Ox decides:</strong> any installer can be offered the lead — including trainees who are being approved in principle. Unapproved offers are tagged for audit.
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAssignLead(null)} data-testid="lm-assign-cancel">Cancel</Button>
              <Button
                size="sm"
                className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white"
                onClick={submitAssign}
                disabled={submittingAssign}
                data-testid="lm-assign-submit"
              >
                <Send className="w-4 h-4 mr-1.5" /> {submittingAssign ? 'Offering…' : 'Send Offers'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add-lead modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="lm-add-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#7A0B7A]" /> Add a new lead
              </h2>
              <button type="button" onClick={closeAdd} className="text-gray-400 hover:text-gray-700" data-testid="lm-add-close">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label className="text-xs font-semibold text-gray-700">Service</Label>
                <div className="flex gap-2 mt-1">
                  {Object.entries(SECTION_LABEL).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewLead({ ...newLead, service_type: key })}
                      className={`flex-1 text-xs font-semibold px-3 py-2 rounded-lg border-2 transition-all ${
                        newLead.service_type === key
                          ? 'border-transparent text-white shadow'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                      style={newLead.service_type === key ? { background: SECTION_COLOR[key] } : {}}
                      data-testid={`lm-add-service-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="lm-cust-name" className="text-xs font-semibold text-gray-700">Customer name *</Label>
                <Input id="lm-cust-name" data-testid="lm-add-name" value={newLead.customer_name}
                  onChange={(e) => setNewLead({ ...newLead, customer_name: e.target.value })} placeholder="e.g. Jane Smith" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="lm-cust-phone" className="text-xs font-semibold text-gray-700">Phone</Label>
                  <Input id="lm-cust-phone" data-testid="lm-add-phone" value={newLead.customer_phone}
                    onChange={(e) => setNewLead({ ...newLead, customer_phone: e.target.value })} placeholder="07700 900000" />
                </div>
                <div>
                  <Label htmlFor="lm-cust-email" className="text-xs font-semibold text-gray-700">Email</Label>
                  <Input id="lm-cust-email" data-testid="lm-add-email" type="email" value={newLead.customer_email}
                    onChange={(e) => setNewLead({ ...newLead, customer_email: e.target.value })} placeholder="jane@example.com" />
                </div>
              </div>
              <div>
                <Label htmlFor="lm-cust-addr" className="text-xs font-semibold text-gray-700">Address / postcode</Label>
                <Input id="lm-cust-addr" data-testid="lm-add-address" value={newLead.customer_address}
                  onChange={(e) => setNewLead({ ...newLead, customer_address: e.target.value })} placeholder="1 Sample Lane, M1 4AB" />
              </div>
              <div>
                <Label htmlFor="lm-cust-source" className="text-xs font-semibold text-gray-700">How did they hear about us?</Label>
                <select
                  id="lm-cust-source"
                  data-testid="lm-add-source"
                  value={newLead.source}
                  onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-2.5 text-sm bg-white"
                >
                  <option value="">— not asked / unknown —</option>
                  {LEAD_SOURCES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">Tracked on the Analytics dashboard so you can see which channels actually convert.</p>
              </div>
              <div>
                <Label htmlFor="lm-cust-desc" className="text-xs font-semibold text-gray-700">Project notes</Label>
                <Textarea id="lm-cust-desc" data-testid="lm-add-description" rows={3} value={newLead.project_description}
                  onChange={(e) => setNewLead({ ...newLead, project_description: e.target.value })}
                  placeholder="e.g. Front driveway ≈ 65m² · wants quote within a week" />
              </div>
              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-md px-3 py-2 text-[11px] text-amber-900">
                <strong>What happens next:</strong> the lead lands in <strong>New / Unassigned</strong>. Click <em>Assign</em> on the card to offer it to one or more installer account codes.
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={closeAdd} data-testid="lm-add-cancel">Cancel</Button>
              <Button
                size="sm"
                className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white"
                onClick={submitAdd}
                disabled={creating}
                data-testid="lm-add-submit"
              >
                <Plus className="w-4 h-4 mr-1.5" /> {creating ? 'Adding…' : 'Add lead'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <LeadCsvImportModal open={showImport} onClose={() => setShowImport(false)} onImported={load} />
    </div>
  );
}

function LeadCard({ lead, column, onAssign, onMatch, onDelete, onArchive, onRestore }) {
  const sec = lead.service_type;
  const tone = SECTION_COLOR[sec] || '#666';
  const enquiry = lead.enquiry || {};
  const pendingOffers = (lead.offers || []).filter((o) => o.status === 'pending');
  const accepted = lead.accepted_offer;
  const declined = (lead.offers || []).filter((o) => ['declined', 'expired', 'cancelled'].includes(o.status));
  const isArchived = column === 'archived';

  return (
    <Card className="p-3 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid={`lm-card-${lead.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full" style={{ background: tone }}>
          {SECTION_LABEL[sec] || sec}
        </span>
        <div className="flex items-center gap-1.5">
          <code className="text-[10px] font-bold text-gray-700">{lead.reference || '—'}</code>
          {!isArchived && (
            <button
              onClick={(e) => { e.stopPropagation(); onArchive(); }}
              data-testid={`lm-archive-${lead.id}`}
              title={`Archive ${lead.customer_name}`}
              className="text-gray-300 hover:text-amber-600 transition-colors p-0.5 rounded"
              aria-label="Archive lead"
            >
              <Archive className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            data-testid={`lm-delete-${lead.id}`}
            title={`Delete lead ${lead.customer_name}`}
            className="text-gray-300 hover:text-red-600 transition-colors p-0.5 rounded"
            aria-label="Delete lead"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <h3 className="text-sm font-bold text-gray-900 truncate">{lead.customer_name || 'Customer'}</h3>
      <div className="text-[11px] text-gray-500 truncate flex items-center gap-1 mt-0.5">
        <MapPin className="w-3 h-3 shrink-0" />
        <span>{lead.customer_postcode || lead.customer_address || '—'}</span>
      </div>
      <div className="text-[11px] text-gray-500 truncate flex items-center gap-1 mt-0.5">
        <Mail className="w-3 h-3 shrink-0" /><span className="truncate">{lead.customer_email || '—'}</span>
      </div>
      {lead.customer_phone && (
        <div className="text-[11px] text-gray-500 truncate flex items-center gap-1 mt-0.5">
          <Phone className="w-3 h-3 shrink-0" /><span>{lead.customer_phone}</span>
        </div>
      )}

      {/* Compact enquiry summary */}
      {(enquiry.project_type || enquiry.area || enquiry.timing) && (
        <div className="mt-2 text-[11px] text-gray-700 bg-gray-50 rounded p-1.5 leading-tight">
          {enquiry.project_type && <div><strong>Type:</strong> {enquiry.project_type}</div>}
          {enquiry.area && <div><strong>Size:</strong> {enquiry.area}</div>}
          {enquiry.timing && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {enquiry.timing}</div>}
        </div>
      )}

      {/* Offer summary */}
      {pendingOffers.length > 0 && (
        <div className="mt-2 text-[11px]">
          <div className="font-semibold text-amber-900 mb-0.5">{pendingOffers.length} pending</div>
          {pendingOffers.slice(0, 3).map((o) => (
            <div key={o.id} className="text-amber-800 truncate flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {o.installer_name || o.installer_account_code}{o.installer_account_code ? ` · ${o.installer_account_code}` : ''}
            </div>
          ))}
        </div>
      )}
      {accepted && (
        <div className="mt-2 text-[11px] text-emerald-800 flex items-center gap-1 font-semibold">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Accepted by {accepted.installer_name}{accepted.installer_account_code ? ` · ${accepted.installer_account_code}` : ''}
        </div>
      )}
      {column === 'rejected' && declined.length > 0 && (
        <div className="mt-2 text-[11px] text-rose-800">
          <div className="flex items-center gap-1 font-semibold mb-0.5"><XCircle className="w-3.5 h-3.5" /> All declined</div>
          {declined.slice(0, 3).map((o) => (
            <div key={o.id} className="text-rose-700 truncate">
              {o.installer_name}{o.decline_reason ? ` — “${o.decline_reason}”` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Action */}
      {isArchived ? (
        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2.5 h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          onClick={onRestore}
          data-testid={`lm-restore-${lead.id}`}
        >
          <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" />
          Restore to active board
        </Button>
      ) : (column === 'unassigned' || column === 'pending' || column === 'rejected') && (
        <div className="flex gap-1.5 mt-2.5">
          <Button
            size="sm"
            className="flex-1 h-8 text-xs bg-[#006400] hover:bg-[#004d00] text-white"
            onClick={onMatch}
            data-testid={`lm-match-${lead.id}`}
          >
            <MapPin className="w-3.5 h-3.5 mr-1.5" />
            Find installers
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={onAssign}
            data-testid={`lm-assign-${lead.id}`}
            title="Offer by account code"
          >
            <UserPlus className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </Card>
  );
}
